import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { database } from '@/firebase';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
} from 'firebase/firestore';
import { useAuthStore } from './auth';
import { GoogleGenerativeAI } from '@google/generative-ai';

/**
 * Env override first, then lighter / alternate models to reduce 503 overload
 * and give 429 rate limits time to recover between attempts.
 */
function modelCandidates() {
  const envModel = process.env.VUE_APP_GEMINI_MODEL;
  const fallbacks = [
    'gemini-2.5-flash-lite',
    'gemini-1.5-flash-8b',
    'gemini-2.0-flash',
    'gemini-flash-latest',
    'gemini-2.5-flash',
  ];
  const ordered = [envModel, ...fallbacks].filter(
    (m) => typeof m === 'string' && m.trim().length > 0
  );
  return [...new Set(ordered.map((m) => m.trim()))];
}

function httpStatus(err) {
  const direct =
    err?.status ??
    err?.statusCode ??
    err?.response?.status ??
    err?.cause?.status ??
    null;
  if (direct != null) return Number(direct);
  const m = String(err?.message || err || '');
  const match = m.match(/\b(401|403|404|429|500|503)\b/);
  return match ? Number(match[1]) : null;
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function backoffAfterFailure(status) {
  if (status === 429) return 2200 + Math.floor(Math.random() * 800);
  if (status === 503) return 1000 + Math.floor(Math.random() * 800);
  if (status === 500) return 700;
  return 350;
}

export const useStoryStore = defineStore('story', () => {
  const step = ref(1);

  const selectedGenre = ref([]);
  const selectedAudience = ref('adult');
  const selectedGender = ref('male');
  const selectedLanguage = ref('english');

  const generatedText = ref('');
  const generatedStory = ref('');
  const isGenerating = ref(false);
  const error = ref(null);

  const stories = ref([]);
  const isLoadingHistory = ref(false);

  const options = computed(() => ({
    genre: selectedGenre.value,
    audience: selectedAudience.value,
    gender: selectedGender.value,
    language: selectedLanguage.value,
  }));

  function buildPromptText() {
    let text = `Write a story for ${selectedAudience.value} ${selectedGender.value} in ${selectedLanguage.value}.`;
    if (selectedGenre.value.length > 0) {
      text += ` The genre of the story is ${selectedGenre.value.join(', ')}.`;
    }
    generatedText.value = text;
  }

  async function generateStory() {
    error.value = null;
    isGenerating.value = true;
    const apiKey = process.env.VUE_APP_GEMINI_API_KEY;
    if (!apiKey) {
      error.value =
        'Missing VUE_APP_GEMINI_API_KEY. Add it to your .env file.';
      isGenerating.value = false;
      return;
    }

    const genAI = new GoogleGenerativeAI(apiKey);
    const prompt = generatedText.value;
    const models = modelCandidates();
    const failures = [];

    try {
      for (let i = 0; i < models.length; i++) {
        const modelName = models[i];
        try {
          const model = genAI.getGenerativeModel({
            model: modelName,
            systemInstruction:
              'You are a Story writer. Do not include any sexual or adult content. The generated story should not exceed 1500 characters.',
          });
          const result = await model.generateContent(prompt);
          const text = result.response.text();
          if (text && text.trim()) {
            generatedStory.value = text;
            return;
          }
          failures.push(`${modelName}: empty response`);
        } catch (e) {
          failures.push(`${modelName}: ${e?.message || e}`);
          const st = httpStatus(e);
          if (st === 401 || st === 403) {
            error.value =
              'Gemini API rejected the key (401/403). Check VUE_APP_GEMINI_API_KEY in Google AI Studio.';
            return;
          }
          const hasMore = i < models.length - 1;
          if (hasMore) {
            await delay(backoffAfterFailure(st));
          }
        }
      }

      error.value =
        failures.length > 0
          ? `All Gemini models failed. ${fallbackHint()} Details: ${failures.slice(-4).join(' | ')}`
          : 'Failed to generate story.';
    } catch (e) {
      error.value = e.message || 'Failed to generate story';
    } finally {
      isGenerating.value = false;
    }
  }

  function fallbackHint() {
    return 'Retry shortly, confirm billing for the Generative Language API, or set VUE_APP_GEMINI_MODEL to a model listed for your key in Google AI Studio.';
  }

  async function saveStory(title) {
    const authStore = useAuthStore();
    const data = {
      user_id: authStore.user.uid,
      title,
      selectedGenre: selectedGenre.value,
      selectedAudiance: selectedAudience.value,
      selectedGender: selectedGender.value,
      selectedLanguage: selectedLanguage.value,
      generatedText: generatedText.value,
      story: generatedStory.value,
      timestamp: Date.now(),
    };
    await addDoc(collection(database, 'userStories'), data);
  }

  async function loadStories() {
    const authStore = useAuthStore();
    if (!authStore.user) return;
    isLoadingHistory.value = true;
    try {
      const q = query(
        collection(database, 'userStories'),
        where('user_id', '==', authStore.user.uid),
        orderBy('timestamp', 'desc')
      );
      const snapshot = await getDocs(q);
      stories.value = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (e) {
      error.value = e.message || 'Failed to load stories';
    } finally {
      isLoadingHistory.value = false;
    }
  }

  function resetWizard() {
    step.value = 1;
    selectedGenre.value = [];
    selectedAudience.value = 'adult';
    selectedGender.value = 'male';
    selectedLanguage.value = 'english';
    generatedText.value = '';
    generatedStory.value = '';
    error.value = null;
  }

  return {
    step,
    selectedGenre,
    selectedAudience,
    selectedGender,
    selectedLanguage,
    generatedText,
    generatedStory,
    isGenerating,
    error,
    stories,
    isLoadingHistory,
    options,
    buildPromptText,
    generateStory,
    saveStory,
    loadStories,
    resetWizard,
  };
});
